import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PenLine, Upload, Send, Plus, Trash2, Loader, FileCheck, Clock, CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';
import { signaturesApi } from '../../utils/api';

const STATUT = {
  envoye: { label: 'En attente', pill: 'bg-amber-50 text-amber-700' },
  signe: { label: 'Signé', pill: 'bg-emerald-50 text-emerald-600' },
  refuse: { label: 'Refusé', pill: 'bg-red-50 text-red-600' },
};
const ROLE_LABEL = { parent1: 'Parent 1', parent2: 'Parent 2', gestionnaire: 'La crèche', autre: 'Signataire' };

export default function SignaturesPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [titre, setTitre] = useState('');
  const [file, setFile] = useState(null);
  const [signers, setSigners] = useState([{ role: 'autre', nom: '', email: '' }]);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    signaturesApi.listAll().then(r => setData(r.data)).catch(() => setData({ configured: false, groupes: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSigner = (i, patch) => setSigners(list => list.map((s, j) => j === i ? { ...s, ...patch } : s));
  const addSigner = () => setSigners(list => [...list, { role: 'autre', nom: '', email: '' }]);
  const removeSigner = (i) => setSigners(list => list.filter((_, j) => j !== i));

  const envoyer = async () => {
    const liste = signers.map(s => ({ ...s, email: (s.email || '').trim(), nom: (s.nom || '').trim() })).filter(s => s.email);
    if (!file) { setMsg({ type: 'error', text: 'Choisissez un document PDF.' }); return; }
    if (!liste.length) { setMsg({ type: 'error', text: 'Renseignez au moins un signataire.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('titre', titre || file.name);
      fd.append('signataires', JSON.stringify(liste));
      const { data: r } = await signaturesApi.envoyerLibre(fd);
      setMsg({ type: 'ok', text: r.message || 'Document envoyé en signature.' });
      setTitre(''); setFile(null); if (fileRef.current) fileRef.current.value = ''; setSigners([{ role: 'autre', nom: '', email: '' }]);
      load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.error || 'Envoi impossible' }); }
    setBusy(false);
  };

  const groupes = data?.groupes || [];

  return (
    <div className="max-w-4xl">
      <div className="mb-4">
        <h1 className="font-serif text-[28px] leading-tight text-navy-900 flex items-center gap-2"><PenLine size={24} className="text-gold-600" /> Signature électronique</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
        <p className="text-navy-400 text-[13.5px]">Faites signer conventions, contrats ou tout autre document, en ligne et de façon sécurisée (conforme au droit français).</p>
      </div>

      {data && !data.configured && (
        <div className="text-[13px] text-amber-700 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2 mb-4">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>L'envoi d'e-mails (SMTP) n'est pas configuré : impossible d'envoyer des demandes de signature pour le moment.</span>
        </div>
      )}

      {/* Nouveau document à signer */}
      <div className="card p-5 mb-5">
        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Upload size={16} className="text-primary-500" /> Faire signer un document</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Document (PDF)</label>
              <input ref={fileRef} type="file" accept=".pdf" onChange={e => setFile(e.target.files[0] || null)}
                className="block w-full text-sm text-navy-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-navy-700 file:text-white file:text-sm hover:file:bg-navy-800" />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Intitulé</label>
              <input value={titre} onChange={e => setTitre(e.target.value)} placeholder="ex. Convention de réservation — Dupont"
                className="form-input rounded-xl w-full" />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Signataires</label>
            <div className="space-y-2">
              {signers.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <input value={s.email} onChange={e => setSigner(i, { email: e.target.value })} placeholder="email@exemple.fr"
                    className="form-input rounded-lg text-[13px] flex-1 min-w-[180px]" />
                  <input value={s.nom} onChange={e => setSigner(i, { nom: e.target.value })} placeholder="Prénom Nom"
                    className="form-input rounded-lg text-[13px] flex-1 min-w-[140px]" />
                  <select value={s.role} onChange={e => setSigner(i, { role: e.target.value })} className="form-select rounded-lg text-[13px] w-32">
                    <option value="autre">Signataire</option>
                    <option value="parent1">Parent 1</option>
                    <option value="parent2">Parent 2</option>
                    <option value="gestionnaire">La crèche</option>
                  </select>
                  {signers.length > 1 && <button onClick={() => removeSigner(i)} className="text-navy-300 hover:text-red-500 p-1"><Trash2 size={15} /></button>}
                </div>
              ))}
            </div>
            <button onClick={addSigner} className="mt-2 text-[13px] text-primary-600 hover:underline inline-flex items-center gap-1"><Plus size={14} /> Ajouter un signataire</button>
          </div>

          <button onClick={envoyer} disabled={busy} className="apple-btn-gold">
            {busy ? <Loader size={15} className="animate-spin" /> : <Send size={15} />} Envoyer en signature
          </button>
          {msg && <div className={`text-[13px] rounded-lg px-3 py-2 ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</div>}
        </div>
      </div>

      {/* Suivi des signatures */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-700">Demandes de signature</h3>
        <button onClick={load} className="text-[13px] text-navy-500 hover:text-navy-800 inline-flex items-center gap-1"><RefreshCw size={14} /> Actualiser</button>
      </div>
      {!data ? <p className="text-navy-300 text-sm py-6 text-center">Chargement…</p>
        : groupes.length === 0 ? <p className="text-navy-300 text-sm py-6 text-center">Aucune demande de signature pour l'instant.</p>
          : (
            <div className="space-y-2">
              {groupes.map(g => (
                <div key={g.groupe_id} className="card p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-navy-800 text-[14px] truncate">{g.doc_label}{g.cible && g.type !== 'libre' ? ` — ${g.cible}` : ''}</div>
                      <div className="text-[11.5px] text-navy-400">{g.created_at ? String(g.created_at).slice(0, 10) : ''} · {g.signes}/{g.total} signé{g.signes > 1 ? 's' : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${(STATUT[g.statut_global] || STATUT.envoye).pill}`}>{(STATUT[g.statut_global] || STATUT.envoye).label}</span>
                      {g.statut_global === 'signe' && g.signed_pdf_id && (
                        <a href={signaturesApi.getDocumentUrl(g.signed_pdf_id)} target="_blank" rel="noopener noreferrer" className="apple-btn-secondary text-[12px]"><FileCheck size={14} /> Document signé</a>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {g.signataires.map(s => (
                      <span key={s.id} className="inline-flex items-center gap-1 text-[11.5px] text-navy-500 bg-cream-100 rounded-full px-2 py-0.5">
                        {s.statut === 'signe' ? <CheckCircle2 size={12} className="text-emerald-500" /> : <Clock size={12} className="text-amber-500" />}
                        {ROLE_LABEL[s.role] || 'Signataire'} · {s.nom || s.email}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}
